import {
  ButtonGroupContext,
  Button as MuiButton,
  ButtonProps as MuiButtonProps,
} from '@mui/material';
import clsx from 'clsx';
import React, { useCallback, useMemo } from 'react';
import {
  buttonClasses, ButtonColorVariant, ButtonProps,
} from './types';
import { SpinningLoading } from '../animations';

const typeMap: Record<
ButtonColorVariant,
Pick<MuiButtonProps, 'variant' | 'color'>
> = {
  primary: {
    variant: 'contained',
    color: 'primary',
  },
  secondary: {
    variant: 'contained',
    color: 'secondary',
  },
  text: {
    color: 'primary',
    variant: 'text',
  },
  textSecondary: {
    color: 'secondary',
    variant: 'text',
  },
  outlined: {
    variant: 'outlined',
    color: 'secondary',
  },
  destructive: {
    variant: 'contained',
    color: 'error',
  },
  destructiveOutlined: {
    variant: 'outlined',
    color: 'error',
  },
  destructiveText: {
    variant: 'text',
    color: 'error',
  },
  inversePrimary: {
    variant: 'contained',
    color: 'inherit',
  },
  inverseOutlined: {
    variant: 'outlined',
    color: 'inherit',
  },
  inverseText: {
    variant: 'text',
    color: 'inherit',
  },
};

const Button = React.forwardRef((props: ButtonProps, ref: React.RefObject<HTMLButtonElement>) => {
  const { color, variant } = React.useContext(ButtonGroupContext);

  const allProps = useMemo(() => {
    const {
      colorVariant, loading, selected, sx,
    } = props;

    const mergedProps = {
      color: color || typeMap[colorVariant ?? 'primary'].color,
      variant: variant || typeMap[colorVariant ?? 'primary'].variant,
      ...props,
    };

    if (loading) {
      mergedProps.sx = {
        cursor: 'not-allowed',
        ...sx,
      };
    }

    mergedProps.className = clsx(
      props.className,
      {
        [buttonClasses.selected]: selected,
      },
    );

    return mergedProps;
  }, [props, color, variant]);

  const {
    loading, size, onClick, children, value, selected, onChange, colorVariant,
    ...rest
  } = allProps;

  const onClickCb = useCallback(
    (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      if (loading) {
        event.preventDefault();
        return;
      }

      if (selected !== undefined && onChange) {
        onChange(event, value);
      }

      if (onClick) {
        onClick(event);
      }
    },
    [loading, selected, onChange, onClick, value],
  );

  return (
    <MuiButton
      {...rest}
      size={size}
      onClick={onClickCb}
      ref={ref}
    >
      {loading ? (
        <SpinningLoading
          sx={{
            width: size === 'large' ? 20 : 16,
            height: size === 'large' ? 22 : 20,
          }}
        />
      ) : (
        children
      )}
    </MuiButton>
  );
});

export default Button;
